import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  { value: 'manufacturing', label: 'Couch Manufacturing' },
  { value: 'sales', label: 'Buy a Couch' },
  { value: 'reupholstery', label: 'Couch Reupholstery' },
  { value: 'custom', label: 'Custom Design Request' },
];

const couchTypes = [
  { value: 'l-shape', label: 'L-Shape Couch' },
  { value: 'corner', label: 'Corner Unit' },
  { value: 'recliner', label: 'Recliner' },
  { value: 'sofa', label: 'Standard Sofa' },
  { value: 'loveseat', label: 'Loveseat' },
  { value: 'sectional', label: 'Sectional' },
  { value: 'other', label: 'Other' },
];

const budgetRanges = [
  { value: 'under-5k', label: 'Under R5,000' },
  { value: '5k-10k', label: 'R5,000 - R10,000' },
  { value: '10k-15k', label: 'R10,000 - R15,000' },
  { value: '15k-25k', label: 'R15,000 - R25,000' },
  { value: 'over-25k', label: 'Over R25,000' },
];

export const QuotePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    couch_type: '',
    budget: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quote`, formData);
      toast.success('Quote request submitted! We\'ll get back to you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', service: '', couch_type: '', budget: '', description: '' });
    } catch (error) {
      toast.error('Failed to submit quote. Please try again.');
      console.error('Quote form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="quote-page">
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1920&q=80"
            alt="Get Quote"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-leather/80"></div>
        </div>
        <div className="relative text-center z-10 px-4">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-gold font-body text-sm uppercase tracking-widest"
          >
            Free Estimate
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4"
          >
            Get a Quote
          </motion.h1>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="section-padding bg-cream">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={handleSubmit}
                data-testid="quote-form"
                className="bg-white rounded-2xl shadow-card p-8"
              >
                <h2 className="font-heading text-2xl text-leather mb-6">
                  Tell Us About Your Project
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-leather font-body font-medium">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      data-testid="quote-name-input"
                      className="rounded-lg"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-leather font-body font-medium">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      data-testid="quote-email-input"
                      className="rounded-lg"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-leather font-body font-medium">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+27 XX XXX XXXX"
                      data-testid="quote-phone-input"
                      className="rounded-lg"
                    />
                  </div>

                  {/* Service */}
                  <div className="space-y-2">
                    <Label className="text-leather font-body font-medium">
                      Service Required *
                    </Label>
                    <Select value={formData.service} onValueChange={(v) => handleSelectChange('service', v)}>
                      <SelectTrigger data-testid="quote-service-select" className="rounded-lg">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Couch Type */}
                  <div className="space-y-2">
                    <Label className="text-leather font-body font-medium">
                      Couch Type (Optional)
                    </Label>
                    <Select value={formData.couch_type} onValueChange={(v) => handleSelectChange('couch_type', v)}>
                      <SelectTrigger data-testid="quote-type-select" className="rounded-lg">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {couchTypes.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Budget */}
                  <div className="space-y-2">
                    <Label className="text-leather font-body font-medium">
                      Budget Range (Optional)
                    </Label>
                    <Select value={formData.budget} onValueChange={(v) => handleSelectChange('budget', v)}>
                      <SelectTrigger data-testid="quote-budget-select" className="rounded-lg">
                        <SelectValue placeholder="Select budget" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgetRanges.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2 mb-8">
                  <Label htmlFor="description" className="text-leather font-body font-medium">
                    Project Details *
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe your requirements, dimensions, preferred materials, colors, etc."
                    rows={6}
                    data-testid="quote-description-input"
                    className="rounded-lg resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="quote-submit-btn"
                  className="w-full md:w-auto btn-gold rounded-full px-10 py-3 text-base font-semibold"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Quote Request
                      <i className="fas fa-paper-plane ml-2"></i>
                    </>
                  )}
                </Button>
              </motion.form>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="border-0 shadow-card">
                  <CardContent className="p-6">
                    <h3 className="font-heading text-xl text-leather mb-4">
                      Need Immediate Help?
                    </h3>
                    <div className="space-y-4">
                      <a 
                        href="https://wa.me/27752781104"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 p-4 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center">
                          <i className="fab fa-whatsapp text-white text-xl"></i>
                        </div>
                        <div>
                          <p className="font-heading font-semibold text-leather">WhatsApp Us</p>
                          <p className="text-leather/60 font-body text-sm">+27 75 278 1104</p>
                        </div>
                      </a>
                      <a 
                        href="tel:+27752781104"
                        className="flex items-center gap-4 p-4 rounded-xl bg-gold/10 hover:bg-gold/20 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-gold flex items-center justify-center">
                          <i className="fas fa-phone text-white text-lg"></i>
                        </div>
                        <div>
                          <p className="font-heading font-semibold text-leather">Call Us</p>
                          <p className="text-leather/60 font-body text-sm">+27 75 278 1104</p>
                        </div>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* What to Expect */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="border-0 shadow-card">
                  <CardContent className="p-6">
                    <h3 className="font-heading text-xl text-leather mb-4">
                      What to Expect
                    </h3>
                    <ul className="space-y-3">
                      {[
                        'Response within 24 hours',
                        'Free consultation call',
                        'Detailed written quote',
                        'No obligation',
                        'Competitive pricing'
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-leather/70 font-body text-sm">
                          <i className="fas fa-check-circle text-gold"></i>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default QuotePage;
